import React, { useEffect, useRef } from 'react';
import { CharReveal, Kicker } from './ui';
const HERO_IMG = 'https://cdn.magicpatterns.com/uploads/3CV7WsPojpKLm3B6p1jMF3/Untitled_-_July_07,_2026_at_12.53.06.png';
const MARQUEE_ITEMS = ['Brand Identity', 'Web Design', 'Motion', 'Product', 'Content', 'Strategy'];
function MarqueeContent() {
  return <span className="flex shrink-0 items-center" data-id="element-53">
      {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => <span key={i} className="flex items-center font-display uppercase text-lg md:text-2xl" data-id="element-54">
          <span className="px-5 md:px-8" data-id="element-55">{item}</span>
          <span aria-hidden="true" data-id="element-56">✦</span>
        </span>)}
    </span>;
}
/**
 * Cursor-reactive mesh: a grid of lines drawn on a canvas where every
 * vertex near the pointer is pushed outward with a radial falloff, so the
 * net appears to warp around the cursor. The pointer position is lerped
 * each frame for a fluid, trailing feel. A brighter clipped pass makes the
 * grid glow slightly around the cursor. On touch devices (no mouse yet),
 * the warp point drifts on a slow orbit so the hero is never static.
 */
function MeshCanvas() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const reduce = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let w = 0;
    let h = 0;
    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      const r = parent.getBoundingClientRect();
      w = r.width;
      h = r.height;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const GAP = 52;
    const RADIUS = 260;
    const STRENGTH = 40;
    const target = {
      x: w * 0.6,
      y: h * 0.4
    };
    const cur = {
      x: target.x,
      y: target.y
    };
    let hasMouse = false;
    const onMove = (e: MouseEvent) => {
      const r = canvas.getBoundingClientRect();
      target.x = e.clientX - r.left;
      target.y = e.clientY - r.top;
      hasMouse = true;
    };
    const displace = (x: number, y: number) => {
      const dx = x - cur.x;
      const dy = y - cur.y;
      const d = Math.hypot(dx, dy);
      if (d >= RADIUS || d === 0) return {
        x,
        y
      };
      const f = 1 - d / RADIUS;
      const push = f * f * STRENGTH;
      return {
        x: x + dx / d * push,
        y: y + dy / d * push
      };
    };
    const drawGrid = (alpha: number) => {
      ctx.lineWidth = 1;
      ctx.strokeStyle = `rgba(239, 234, 227, ${alpha})`;
      for (let y = 0; y <= h + GAP; y += GAP) {
        ctx.beginPath();
        for (let x = 0; x <= w + GAP; x += GAP) {
          const p = displace(x, y);
          if (x === 0) ctx.moveTo(p.x, p.y);else ctx.lineTo(p.x, p.y);
        }
        ctx.stroke();
      }
      for (let x = 0; x <= w + GAP; x += GAP) {
        ctx.beginPath();
        for (let y = 0; y <= h + GAP; y += GAP) {
          const p = displace(x, y);
          if (y === 0) ctx.moveTo(p.x, p.y);else ctx.lineTo(p.x, p.y);
        }
        ctx.stroke();
      }
    };
    if (reduce) {
      // Static grid, no warp, no loop.
      cur.x = -9999;
      cur.y = -9999;
      drawGrid(0.06);
      window.addEventListener('resize', () => {
        resize();
        drawGrid(0.06);
      });
      return;
    }
    let raf = 0;
    const loop = (now: number) => {
      // Idle orbit until the first real mouse move (covers touch devices).
      if (!hasMouse) {
        target.x = w * 0.5 + Math.cos(now / 2600) * w * 0.28;
        target.y = h * 0.42 + Math.sin(now / 2100) * h * 0.2;
      }
      cur.x += (target.x - cur.x) * 0.08;
      cur.y += (target.y - cur.y) * 0.08;
      const rect = canvas.getBoundingClientRect();
      if (rect.bottom > 0 && rect.top < (window.innerHeight || 800)) {
        ctx.clearRect(0, 0, w, h);
        drawGrid(0.065);
        // Brighter pass clipped to a circle around the cursor.
        ctx.save();
        ctx.beginPath();
        ctx.arc(cur.x, cur.y, RADIUS * 0.75, 0, Math.PI * 2);
        ctx.clip();
        drawGrid(0.16);
        ctx.restore();
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('resize', resize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('resize', resize);
    };
  }, []);
  return <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none" aria-hidden="true" data-id="element-57" />;
}
export function Hero() {
  return <section id="top" className="relative" data-id="element-58">
      <div className="relative flex min-h-screen flex-col justify-between overflow-hidden" style={{
      background: 'var(--ink)'
    }} data-id="element-59">
        <img src={HERO_IMG} alt="Futuristic Lagos cityscape at dusk with elevated transit, neon billboards and yellow danfo buses" className="absolute inset-0 h-full w-full object-cover" data-id="element-60" />
        <span className="absolute inset-0" aria-hidden="true" style={{
        background: 'linear-gradient(180deg, rgba(17,16,19,0.72) 0%, rgba(17,16,19,0.25) 34%, rgba(17,16,19,0.3) 62%, rgba(17,16,19,0.86) 100%)'
      }} data-id="element-61" />
        <MeshCanvas data-id="element-62" />

        <div className="relative z-10 mx-auto w-full max-w-[1400px] px-5 md:px-10 pt-24 md:pt-36" data-id="element-63">
          <div className="grid gap-10 md:grid-cols-2" data-id="element-64">
            <div data-id="element-65">
              <Kicker ig="Ndị Okike" en="Creative studio" data-id="element-66" />
              <p className="font-display mt-4 text-3xl md:text-5xl tracking-tight" data-id="element-67">
                <CharReveal text="Nkali Studio" step={28} data-id="element-68" />
              </p>
              <p className="font-monoui mt-5 text-[11px] md:text-xs uppercase tracking-[0.2em] leading-loose opacity-70" data-id="element-69">
                14 Adeola Odeku Street
                <br data-id="element-70" />
                Victoria Island, Lagos
                <br data-id="element-71" />
                Nigeria
              </p>
            </div>
          </div>
        </div>

        <div className="relative z-10 mx-auto w-full max-w-[1400px] px-5 md:px-10 pb-10 md:pb-16" data-id="element-72">
          <h1 className="font-display uppercase tracking-tight leading-[0.9] text-[15vw] md:text-[10.5vw]" data-id="element-73">
            <span className="block" style={{
            color: 'var(--danfo)'
          }} data-id="element-74">
              <CharReveal text="Make them" step={30} data-id="element-75" />
              <span className="inline-block" style={{
              width: '0.18em'
            }} aria-hidden="true" data-id="element-76" />
              <CharReveal text="✦" baseDelay={320} data-id="element-77" />
            </span>
            <span className="block" data-id="element-78">
              <CharReveal text="Look twice" baseDelay={340} step={34} data-id="element-79" />
            </span>
          </h1>
          <div className="mt-6 flex items-center justify-between" data-id="element-80">
            <p className="max-w-md text-sm md:text-base leading-relaxed" style={{
            color: 'var(--paper-dim)'
          }} data-id="element-81">
              Brands, websites and motion for companies that want attention they
              can keep.
            </p>
            <a href="#reel" className="font-monoui hidden md:block text-xs uppercase tracking-[0.2em] opacity-70 transition-opacity hover:opacity-100" data-id="element-82">
              Scroll{' '}
              <span style={{
              color: 'var(--danfo)'
            }} data-id="element-83">
                ↓
              </span>
            </a>
          </div>
        </div>
      </div>

      <div className="marquee-hover overflow-hidden border-y py-3 md:py-4" style={{
      background: 'var(--danfo)',
      color: 'var(--ink)',
      borderColor: 'rgba(17,16,19,0.25)'
    }} data-id="element-84">
        <div className="marquee-track flex w-max" data-id="element-85">
          <MarqueeContent data-id="element-86" />
          <MarqueeContent data-id="element-87" />
        </div>
      </div>
    </section>;
}